// KaTeX 数学渲染器

import { MathRenderer, MathRenderOptions } from '../types';
import GlobalKaTeX from '../GlobalKaTeX';

export class KaTeXRenderer implements MathRenderer {
  private katex: any = null;
  private loaded = false;

  async loadKaTeX(): Promise<void> {
    if (this.loaded) return;

    try {
      // 🔧 使用全局KaTeX实例，避免重复加载
      if (GlobalKaTeX.isLoaded()) {
        // 如果全局实例已加载，直接使用
        this.katex = GlobalKaTeX.getKaTeX();
        this.loaded = true;
        return;
      }

      // 如果全局实例未加载，等待加载完成
      this.katex = await GlobalKaTeX.ensureLoaded();
      this.loaded = true;
    } catch (error) {
      console.warn('Failed to load KaTeX:', error);
      throw error;
    }
  }

  isSupported(): boolean {
    return typeof window !== 'undefined';
  }

  async render(latex: string, options?: MathRenderOptions): Promise<string> {
    if (!this.loaded) {
      await this.loadKaTeX();
    }

    if (!this.katex) {
      throw new Error('KaTeX not loaded');
    }

    try {
      // 预处理：将placeholder转换为KaTeX支持的格式
      const processedLatex = this.preprocessLatex(latex);

      const defaultOptions = {
        displayMode: false,
        throwOnError: false,
        errorColor: '#cc0000',
        ...options
      };

      return this.katex.renderToString(processedLatex, defaultOptions);
    } catch (error) {
      console.warn('KaTeX render error:', error);
      if (options?.throwOnError) {
        throw error;
      }
      return `<span class="math-error">${latex}</span>`;
    }
  }

  async renderToElement(latex: string, element: HTMLElement, options?: MathRenderOptions): Promise<void> {
    if (!this.loaded) {
      await this.loadKaTeX();
    }

    if (!this.katex) {
      throw new Error('KaTeX not loaded');
    }

    try {
      // 预处理：将placeholder转换为KaTeX支持的格式
      const processedLatex = this.preprocessLatex(latex);

      const defaultOptions = {
        displayMode: false,
        throwOnError: false,
        errorColor: '#cc0000',
        ...options
      };

      this.katex.render(processedLatex, element, defaultOptions);
    } catch (error) {
      console.warn('KaTeX render error:', error);
      if (options?.throwOnError) {
        throw error;
      }
      element.innerHTML = `<span class="math-error">${latex}</span>`;
    }
  }

  /**
   * 预处理LaTeX，将不支持的命令转换为KaTeX支持的格式
   */
  private preprocessLatex(latex: string): string {
    let processed = latex;

    // 将 \placeholder{content} 直接替换为内容，不加边框
    processed = processed.replace(/\\placeholder\{([^}]+)\}/g, '$1');

    return processed;
  }
}